import { BookmarkIcon } from 'lucide-react';

import { Avatar, type AvatarSize } from '@/shared/components/avatar';
import { cn } from '@/shared/lib/cn';

import type { ChatType, ChatUser } from '../types';

export interface ChatAvatarProps {
  chat: { id: string; type: ChatType; title: string; avatarUrl: string | null; peer: ChatUser | null };
  size?: AvatarSize;
  /** Presence dot for direct chats (live through realtime). */
  showPresence?: boolean;
  className?: string;
}

const SAVED_SIZE: Record<AvatarSize, string> = {
  xs: 'size-5 rounded-md [&_svg]:size-3',
  sm: 'size-6 rounded-md [&_svg]:size-3.5',
  md: 'size-8 rounded-lg [&_svg]:size-4',
  lg: 'size-10 rounded-lg [&_svg]:size-5',
  xl: 'size-14 rounded-xl [&_svg]:size-7',
  '2xl': 'size-20 rounded-2xl [&_svg]:size-10',
};

/** A person's avatar for direct chats (with presence), a bookmark tile for the saved chat, a rounded tile for group chats. */
export function ChatAvatar({ chat, size = 'lg', showPresence = true, className }: ChatAvatarProps) {
  if (chat.type === 'saved') {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center bg-brand text-brand-foreground',
          SAVED_SIZE[size],
          className,
        )}
        data-testid="saved-avatar"
        aria-hidden
      >
        <BookmarkIcon />
      </span>
    );
  }
  if (chat.type === 'direct' && chat.peer) {
    return (
      <Avatar
        user={{ ...chat.peer, isOnline: chat.peer.isOnline ?? undefined }}
        size={size}
        showPresence={showPresence}
        className={className}
      />
    );
  }
  return (
    <Avatar
      name={chat.title}
      src={chat.avatarUrl}
      seed={chat.id}
      size={size}
      shape="rounded"
      className={className}
    />
  );
}
